"use client";

import { useId, useMemo, useState, type ComponentPropsWithRef } from "react";

import { Alert } from "@/components/ui/alert";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input, Textarea } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { RECOMMENDATION_LABELS, RECOMMENDATION_OPTIONS, defaultEventLabel, type ScorecardField } from "../../lib/types";
import { ScorecardSelect } from "../shared";

type Values = Record<string, unknown>;

/** Matches ScorecardFillCreate.external_event_id @maxLength 120 */
const EXTERNAL_EVENT_ID_MAX_LENGTH = 120;

const NO_OPTIONS_HINT = "This field has no options configured — edit the scorecard type.";

interface Props {
  fields: ScorecardField[];
  initialValues?: Values;
  initialLabel?: string;
  showLabel: boolean;
  onSaveDraft: (values: Values, label: string) => void | Promise<void>;
  onSubmit: (values: Values, label: string) => void | Promise<void>;
  busy?: boolean;
  existsConflict?: { existingFillId?: string; onOpenExisting: (id: string) => void };
}

function hasNoChoices(f: ScorecardField): boolean {
  if (f.type === "rubric_score") return !f.levels?.length;
  if (f.type === "single_choice") return !f.options?.length;
  return false;
}

export function ScorecardFillForm({ fields, initialValues, initialLabel, showLabel, onSaveDraft, onSubmit, busy, existsConflict }: Props) {
  const missingHintId = useId();
  const labelHintId = useId();
  const malformedIdPrefix = useId();
  const labelInputId = useId();
  const [values, setValues] = useState<Values>(initialValues ?? {});
  const [label, setLabel] = useState(initialLabel ?? defaultEventLabel());
  const set = (k: string, v: unknown) => setValues((prev) => ({ ...prev, [k]: v }));
  const toggle = (k: string, v: unknown, required: boolean) => {
    setValues((prev) => {
      if (prev[k] === v) {
        if (required) return prev;
        return { ...prev, [k]: null };
      }
      return { ...prev, [k]: v };
    });
  };
  // Only a *required* field with no configured choices blocks Submit — an
  // optional one can simply be left empty; the notice still renders on the field.
  const malformed = useMemo(() => fields.filter((f) => f.required && hasNoChoices(f)), [fields]);
  const missing = useMemo(
    () => fields.filter((f) => f.required && !hasNoChoices(f) && (values[f.key] == null || values[f.key] === "")).map((f) => f.label),
    [fields, values],
  );
  const labelTooLong = showLabel && label.trim().length > EXTERNAL_EVENT_ID_MAX_LENGTH;
  const labelEmpty = showLabel && label.trim().length === 0;
  const labelInvalid = labelTooLong || labelEmpty;
  const canSubmit = missing.length === 0 && malformed.length === 0 && !labelInvalid;
  const conflictId = existsConflict?.existingFillId;
  const malformedId = (key: string) => `${malformedIdPrefix}-${key}`;
  const submitDescribedBy = [
    missing.length > 0 ? missingHintId : undefined,
    labelInvalid ? labelHintId : undefined,
    ...malformed.map((f) => malformedId(f.key)),
  ].filter((id): id is string => Boolean(id));
  const draftDescribedBy = labelInvalid ? labelHintId : undefined;

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4">
        {showLabel && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-contrast/60" htmlFor={labelInputId}>
              Event label
            </label>
            <Input
              id={labelInputId}
              aria-label="Event label"
              variant="modal"
              size="sm"
              value={label}
              maxLength={EXTERNAL_EVENT_ID_MAX_LENGTH}
              disabled={busy}
              onChange={(e) => setLabel(e.target.value)}
            />
            {labelInvalid && (
              <p id={labelHintId} className="text-xs text-destructive">
                {labelTooLong
                  ? `Must be ${EXTERNAL_EVENT_ID_MAX_LENGTH} characters or fewer.`
                  : "Event label is required."}
              </p>
            )}
            <p className="text-[13px] text-contrast/50">A second scorecard on the same type needs a different label.</p>
          </div>
        )}

        {existsConflict && (
          <Alert.Container role="alert" variant="warning" dir="vertical" size="sm">
            <Alert.Icon>
              <Icon name="IconWarningSign" size={16} aria-hidden="true" />
            </Alert.Icon>
            <Alert.Content>
              {conflictId
                ? "You already have a scorecard for this type and event."
                : "You already have a scorecard of this type for this interview."}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {conflictId && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => existsConflict.onOpenExisting(conflictId)}
                  >
                    Open the existing scorecard
                  </Button>
                )}
                <span className="text-xs text-contrast/50">or change the label above</span>
              </div>
            </Alert.Content>
          </Alert.Container>
        )}

        {fields.map((f) => (
          <fieldset key={f.key} className="min-w-0 border-0 p-0">
            <legend className="text-xs font-medium text-contrast/60">
              {f.label}
              {f.required && <span className="text-destructive"> *</span>}
            </legend>
            {f.help && <p className="mt-0.5 text-[13px] text-contrast/50">{f.help}</p>}
            {hasNoChoices(f) && (
              <p id={malformedId(f.key)} role="alert" className="mt-1 text-xs text-destructive">{NO_OPTIONS_HINT}</p>
            )}
            {f.type === "rubric_score" && (
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(f.levels ?? []).map((l) => (
                  <Tooltip key={l.value}>
                    <TooltipTrigger asChild>
                      <ChoiceCard
                        selected={values[f.key] === l.value}
                        disabled={busy}
                        label={`${l.value} — ${l.anchor}`}
                        onClick={() => toggle(f.key, l.value, f.required)}
                      >
                        <span className="text-sm leading-5">{String(l.value)}</span>
                        {l.anchor && (
                          <span className="max-w-[7rem] truncate text-[11px] font-normal opacity-70">
                            {l.anchor}
                          </span>
                        )}
                      </ChoiceCard>
                    </TooltipTrigger>
                    <TooltipContent>{l.anchor}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}
            {f.type === "text" && (
              <Textarea
                aria-label={f.label}
                variant="modal"
                className="mt-1.5"
                rows={3}
                disabled={busy}
                value={(values[f.key] as string) ?? ""}
                onChange={(e) => set(f.key, e.target.value || null)}
              />
            )}
            {f.type === "yes_no" && (
              <div className="mt-1.5 flex flex-wrap gap-2">
                <ChoiceCard
                  selected={values[f.key] === true}
                  disabled={busy}
                  label="Yes"
                  onClick={() => toggle(f.key, true, f.required)}
                >
                  <span className="text-sm leading-5">Yes</span>
                </ChoiceCard>
                <ChoiceCard
                  selected={values[f.key] === false}
                  disabled={busy}
                  label="No"
                  onClick={() => toggle(f.key, false, f.required)}
                >
                  <span className="text-sm leading-5">No</span>
                </ChoiceCard>
              </div>
            )}
            {(f.type === "single_choice" || f.type === "overall_recommendation") && (
              <div className="mt-1.5">
                <ScorecardSelect
                  label={f.label}
                  value={(values[f.key] as string) ?? ""}
                  placeholder="—"
                  variant="modal"
                  disabled={busy}
                  className="w-full sm:w-[18rem]"
                  options={(f.type === "overall_recommendation" && !f.options?.length
                    ? [...RECOMMENDATION_OPTIONS]
                    : (f.options ?? [])
                  ).map((o) => ({
                    value: o,
                    label:
                      f.type === "overall_recommendation"
                        ? RECOMMENDATION_LABELS[o as keyof typeof RECOMMENDATION_LABELS] ?? o
                        : o,
                  }))}
                  onChange={(v) => set(f.key, v || null)}
                />
              </div>
            )}
          </fieldset>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy || labelInvalid}
            aria-describedby={draftDescribedBy}
            onClick={() => void onSaveDraft(values, label.trim())}
          >
            Save draft
          </Button>
          <SubmitButton
            type="button"
            variant="light"
            size="sm"
            disabled={busy || !canSubmit}
            aria-describedby={submitDescribedBy.length > 0 ? submitDescribedBy.join(" ") : undefined}
            onClick={() => void onSubmit(values, label.trim())}
          >
            Submit
          </SubmitButton>
          {missing.length > 0 && (
            <span id={missingHintId} className="text-xs text-contrast/50">
              Missing: {missing.join(", ")}
            </span>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * Selectable option card — the `NameModeGrid` card from the privacy settings
 * page (`features/org-privacy-settings/components/page.tsx`), kept as a
 * toggle button so `aria-pressed` still carries the state.
 */
function ChoiceCard({
  selected,
  disabled,
  label,
  onClick,
  children,
  ref,
  ...props
}: ComponentPropsWithRef<"button"> & {
  selected: boolean;
  label: string;
}) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex min-w-[4.5rem] cursor-pointer flex-col items-center justify-center gap-0.5 rounded-md border px-3 py-1.5",
        "outline-none transition-[border-color,background-color] duration-200",
        "focus-visible:ring-2 focus-visible:ring-foreground/25 disabled:cursor-not-allowed disabled:opacity-50",
        selected
          ? "border-foreground/40 bg-foreground/[0.08] font-medium text-heading"
          : "border-foreground/15 bg-foreground/[0.02] text-muted-foreground hover:border-foreground/28 hover:bg-foreground/4",
      )}
      {...props}
    >
      {children}
    </button>
  );
}
